import React, { useEffect, useState } from 'react';
import apiClient, { formatApiError } from '../utils/api';
import { toast } from 'sonner';
import * as PhosphorIcons from '@phosphor-icons/react';

const { ChartBar, TrendUp, Clock, MusicNotes, Play, Sparkle, Queue, Megaphone } = PhosphorIcons;

const Analytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7d');

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/analytics?range=${timeRange}`);
      setData(res.data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="text-[#6b665c] font-mono">Loading analytics...</div>;
  if (!data) return <div className="text-[#8a8477] font-mono">No data available</div>;

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2" data-testid="analytics-title">
            <span className="bg-gradient-to-r from-blue-400 to-pink-400 bg-clip-text text-transparent">Analytics</span>
          </h1>
          <p className="text-[#6b665c] font-mono text-sm tracking-wider uppercase">Music Engagement Insights</p>
        </div>
        <div className="flex gap-2">
          {['24h', '7d', '30d', '90d'].map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              data-testid={`range-${range}`}
              className={`px-4 py-2 rounded-md text-sm font-mono transition-all ${
                timeRange === range
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-[#6b665c] hover:bg-[#e4ebe5]'
              }`}
            >
              {range}
            </button>
          ))}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Total Stores"
          value={data.total_stores}
          sub={`${data.online_stores} online`}
          icon={<TrendUp size={28} weight="fill" className="text-[#7c9082]" />}
          gradient="from-blue-900/20 to-blue-600/10"
          border="border-blue-500/30"
          accent="text-[#7c9082]"
          testId="metric-stores"
        />
        <MetricCard
          label="Music Tracks"
          value={data.total_tracks}
          sub="in library"
          icon={<MusicNotes size={28} weight="fill" className="text-pink-400" />}
          gradient="from-pink-900/20 to-pink-600/10"
          border="border-pink-500/30"
          accent="text-pink-400"
          testId="metric-tracks"
        />
        <MetricCard
          label="Playlists"
          value={data.total_playlists}
          sub="active"
          icon={<Queue size={28} weight="fill" className="text-green-400" />}
          gradient="from-green-900/20 to-green-600/10"
          border="border-green-500/30"
          accent="text-green-400"
          testId="metric-playlists"
        />
        <MetricCard
          label="Dayparts"
          value={data.total_dayparts}
          sub={`${data.total_announcements} announcements`}
          icon={<Clock size={28} weight="fill" className="text-yellow-400" />}
          gradient="from-yellow-900/20 to-yellow-600/10"
          border="border-yellow-500/30"
          accent="text-yellow-400"
          testId="metric-dayparts"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Playlist Performance */}
        <div className="bg-white border border-[#c5d4c8] rounded-md p-6">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <ChartBar size={22} className="text-[#7c9082]" />
            Top Playlists by Track Count
          </h2>
          {data.playlist_performance.length === 0 ? (
            <p className="text-[#8a8477] font-mono text-sm">No playlists yet</p>
          ) : (
            <div className="space-y-3">
              {data.playlist_performance.map((pl, idx) => {
                const maxTracks = Math.max(...data.playlist_performance.map(p => p.track_count), 1);
                const pct = Math.round((pl.track_count / maxTracks) * 100);
                return (
                  <div key={pl.id} className="bg-[#f5f0e8] border border-[#c5d4c8] rounded-md p-4" data-testid={`playlist-perf-${idx}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className={`w-7 h-7 rounded flex items-center justify-center font-bold text-sm ${
                          idx === 0 ? 'bg-yellow-600 text-black' : idx === 1 ? 'bg-zinc-600 text-white' : 'bg-[#e4ebe5] text-[#6b665c]'
                        }`}>{idx + 1}</div>
                        <p className="font-medium text-sm">{pl.name}</p>
                      </div>
                      <span className="text-xs font-mono text-[#6b665c]">{pl.track_count} tracks</span>
                    </div>
                    <div className="w-full bg-[#e4ebe5] rounded-full h-1.5">
                      <div className="bg-gradient-to-r from-blue-500 to-pink-500 h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Store Overview */}
        <div className="bg-white border border-[#c5d4c8] rounded-md p-6">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Sparkle size={22} className="text-pink-400" />
            Store Status Overview
          </h2>
          {data.stores.length === 0 ? (
            <p className="text-[#8a8477] font-mono text-sm">No stores yet</p>
          ) : (
            <div className="space-y-3">
              {data.stores.slice(0, 8).map((store) => (
                <div key={store.id} className="flex items-center justify-between bg-[#f5f0e8] border border-[#c5d4c8] rounded-md p-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-2.5 h-2.5 rounded-full ${store.is_online ? 'bg-green-500' : 'bg-zinc-600'}`} />
                    <div>
                      <p className="text-sm font-medium">{store.name}</p>
                      <p className="text-xs text-[#8a8477] font-mono">{store.location}</p>
                    </div>
                  </div>
                  <span className={`text-xs font-mono px-2 py-0.5 rounded ${
                    store.is_online ? 'bg-green-900/30 text-green-400' : 'bg-[#e4ebe5] text-[#8a8477]'
                  }`}>
                    {store.is_online ? 'Online' : 'Offline'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Track Library Sample */}
      {data.tracks_sample.length > 0 && (
        <div className="bg-white border border-[#c5d4c8] rounded-md p-6">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Play size={22} className="text-purple-400" />
            Recent Tracks in Library
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.tracks_sample.map((track) => (
              <div key={track.id} className="bg-[#f5f0e8] border border-[#c5d4c8] rounded-md p-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded bg-[#e4ebe5] flex items-center justify-center">
                  <MusicNotes size={18} className="text-[#8a8477]" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{track.title}</p>
                  <p className="text-xs text-[#8a8477] font-mono truncate">{track.artist}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary Card */}
      <div className="bg-gradient-to-r from-blue-900/15 to-pink-900/15 border border-blue-500/20 rounded-md p-6">
        <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
          <Sparkle size={22} className="text-yellow-400" />
          Quick Summary
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white/50 rounded-md p-4">
            <p className="text-xs text-[#5a6e5f] font-mono mb-1 uppercase tracking-wider">Coverage</p>
            <p className="text-sm text-[#5a6e5f]">
              {data.total_stores} store{data.total_stores !== 1 ? 's' : ''} configured with {data.total_playlists} playlist{data.total_playlists !== 1 ? 's' : ''} and {data.total_dayparts} daypart schedule{data.total_dayparts !== 1 ? 's' : ''}.
            </p>
          </div>
          <div className="bg-white/50 rounded-md p-4">
            <p className="text-xs text-green-300 font-mono mb-1 uppercase tracking-wider">Library</p>
            <p className="text-sm text-[#5a6e5f]">
              {data.total_tracks} track{data.total_tracks !== 1 ? 's' : ''} uploaded. {data.total_announcements} in-store announcement{data.total_announcements !== 1 ? 's' : ''} active.
            </p>
          </div>
          <div className="bg-white/50 rounded-md p-4">
            <p className="text-xs text-yellow-300 font-mono mb-1 uppercase tracking-wider">Status</p>
            <p className="text-sm text-[#5a6e5f]">
              {data.online_stores} of {data.total_stores} stores online right now.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const MetricCard = ({ label, value, sub, icon, gradient, border, accent, testId }) => (
  <div className={`bg-gradient-to-br ${gradient} border ${border} rounded-md p-6`} data-testid={testId}>
    <div className="flex items-start justify-between mb-2">
      <div>
        <p className={`${accent} font-mono text-xs uppercase tracking-wider mb-2`}>{label}</p>
        <p className="text-3xl font-black text-white">{value}</p>
      </div>
      {icon}
    </div>
    <p className={`text-xs ${accent} font-mono`}>{sub}</p>
  </div>
);

export default Analytics;
